import React, { useState, useEffect } from 'react';
import { FaEdit, FaEye, FaEyeSlash, FaSave, FaTrash } from 'react-icons/fa';
import styles from './styles/SubcategoryCrud.module.css';
import shared from './styles/adminShared.module.css';
import PrimaryButton from '../PrimaryButton';

interface Subcategory {
    idSubcategoria: number;
    nombre: string;
    idCategoria: number;
    nombre_categoria: string;
    imagen_url?: string;
    visible: boolean;
    orden: number;
}

interface Category {
    idCategoria: number;
    nombre: string;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const IMAGE_SIZE_HELP = 'Ideal: imagen cuadrada 1200 x 1200 px, formato JPG/PNG/WEBP y menor a 1 MB.';

const getApiErrorMessage = async (res: Response, fallback: string) => {
    try {
        const data = await res.json();
        return data?.error || data?.message || fallback;
    } catch {
        return fallback;
    }
};

const SubcategoryCrud: React.FC = () => {
    const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [configMessage, setConfigMessage] = useState('');
    const [savingConfig, setSavingConfig] = useState(false);

    const [formData, setFormData] = useState({ idSubcategoria: 0, nombre: '', idCategoria: 0, imagen_url: '' });
    const [isEditing, setIsEditing] = useState(false);

    useEffect(() => {
        fetchSubcategories();
        fetchCategories();
    }, []);

    const fetchSubcategories = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await fetch(`${API_URL}/subcategorias?include_hidden=1`);
            if (!res.ok) {
                throw new Error(await getApiErrorMessage(res, 'Error al cargar subcategorias'));
            }
            const data = await res.json();
            if (Array.isArray(data)) {
                // Deduplicate by ID to prevent UI glitches if API returns dupes
                const uniqueData = data.filter((item, index, self) =>
                    index === self.findIndex((t) => (
                        t.idSubcategoria === item.idSubcategoria
                    ))
                );

                if (uniqueData.length !== data.length) {
                    console.warn(`Duplicate subcategories detected! Original: ${data.length}, Unique: ${uniqueData.length}`);
                }

                const ordered = uniqueData.sort((a, b) =>
                    a.nombre_categoria.localeCompare(b.nombre_categoria, 'es') || a.orden - b.orden
                );
                const positions = new Map<number, number>();
                setSubcategories(ordered.map(subcategory => {
                    const orden = positions.get(subcategory.idCategoria) || 0;
                    positions.set(subcategory.idCategoria, orden + 1);
                    return {
                        ...subcategory,
                        visible: subcategory.visible !== false && Number(subcategory.visible) !== 0,
                        orden
                    };
                }));
            }
        } catch (err) {
            setError('Error al cargar subcategorías');
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const res = await fetch(`${API_URL}/categorias?include_hidden=1`);
            if (!res.ok) {
                throw new Error(await getApiErrorMessage(res, 'Error al cargar categorias'));
            }
            const data = await res.json();
            if (Array.isArray(data)) setCategories(data);
            else {
                setCategories([]);
                setError('La API de categorias devolvio un formato inesperado.');
            }
        } catch (err) {
            console.error('Error cargando categorías', err);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (Number(formData.idCategoria) === 0) {
            setError("Selecciona una categoría válida");
            return;
        }
        setError('');

        const url = isEditing
            ? `${API_URL}/admin/subcategorias/${formData.idSubcategoria}`
            : `${API_URL}/admin/subcategorias`;

        const method = isEditing ? 'PUT' : 'POST';

        try {
            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    nombre: formData.nombre,
                    idCategoria: Number(formData.idCategoria),
                    imagen_url: formData.imagen_url
                })
            });

            if (res.ok) {
                fetchSubcategories();
                resetForm();
            } else {
                setError('Error al guardar la subcategoría');
            }
        } catch (err) {
            setError('Error de conexión');
        }
    };

    const handleEdit = (sub: Subcategory) => {
        setFormData({
            idSubcategoria: sub.idSubcategoria,
            nombre: sub.nombre,
            idCategoria: sub.idCategoria,
            imagen_url: sub.imagen_url || ''
        });
        setIsEditing(true);
    };

    const handleDelete = async (id: number) => {
        setError('');
        try {
            const impactRes = await fetch(`${API_URL}/admin/subcategorias/${id}/impacto`);
            if (!impactRes.ok) {
                throw new Error(await getApiErrorMessage(impactRes, 'No se pudo calcular el impacto de la eliminación'));
            }
            const impact = await impactRes.json();
            const confirmed = window.confirm(
                `ADVERTENCIA: vas a eliminar la subcategoría "${impact.nombre}" ` +
                `de "${impact.categoria || 'Sin categoría'}".\n\n` +
                `• ${impact.productos} producto(s) quedarán sin categoría ni subcategoría.\n` +
                `• Los productos, su stock y su historial NO se eliminarán.\n\n` +
                `Si solo querés reorganizarla, cancelá y usá Editar para mudarla.\n\n` +
                `¿Querés continuar?`
            );
            if (!confirmed) return;

            const res = await fetch(`${API_URL}/admin/subcategorias/${id}`, { method: 'DELETE' });
            if (res.ok) fetchSubcategories();
            else setError(await getApiErrorMessage(res, 'Error al eliminar'));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Error de conexión');
        }
    };

    const resetForm = () => {
        setFormData({ idSubcategoria: 0, nombre: '', idCategoria: 0, imagen_url: '' });
        setIsEditing(false);
        setError('');
    };

    const changeOrder = (subcategory: Subcategory, newPosition: number) => {
        setConfigMessage('');
        setSubcategories(current => {
            const siblings = current
                .filter(item => item.idCategoria === subcategory.idCategoria)
                .sort((a, b) => a.orden - b.orden);
            const currentIndex = siblings.findIndex(item => item.idSubcategoria === subcategory.idSubcategoria);
            if (currentIndex < 0) return current;
            const [selected] = siblings.splice(currentIndex, 1);
            siblings.splice(newPosition, 0, selected);
            const updates = new Map(siblings.map((item, orden) => [item.idSubcategoria, orden]));
            return current.map(item => updates.has(item.idSubcategoria)
                ? { ...item, orden: updates.get(item.idSubcategoria)! }
                : item
            );
        });
    };

    const toggleVisibility = (id: number) => {
        setConfigMessage('');
        setSubcategories(current => current.map(subcategory =>
            subcategory.idSubcategoria === id ? { ...subcategory, visible: !subcategory.visible } : subcategory
        ));
    };

    const saveDisplayConfig = async () => {
        setSavingConfig(true);
        setError('');
        setConfigMessage('');
        try {
            const res = await fetch(`${API_URL}/admin/subcategorias/orden`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    items: subcategories.map(subcategory => ({
                        idSubcategoria: subcategory.idSubcategoria,
                        visible: subcategory.visible,
                        orden: subcategory.orden
                    }))
                })
            });
            if (!res.ok) throw new Error(await getApiErrorMessage(res, 'No se pudo guardar'));
            setConfigMessage('Orden y visibilidad guardados correctamente.');
            await fetchSubcategories();
            window.dispatchEvent(new Event('categories-config-updated'));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'No se pudo guardar');
        } finally {
            setSavingConfig(false);
        }
    };

    return (
        <div className={styles['subcategory-crud-container']}>
            <h3>Gestión de Subcategorías</h3>
            {error && <div className={styles['error-msg']}>{error}</div>}

            <div className={styles['subcategory-crud-form']}>
                <h4>{isEditing ? 'Editar o mudar Subcategoría' : 'Nueva Subcategoría'}</h4>
                <form onSubmit={handleSubmit}>
                    <div className={styles['form-group-row']}>
                        <div className={styles['form-group']}>
                            <label>Categoría:</label>
                            <select name="idCategoria" value={formData.idCategoria} onChange={handleChange} required>
                                <option value={0}>-- Selecciona --</option>
                                {categories.map(c => (
                                    <option key={c.idCategoria} value={c.idCategoria}>{c.nombre}</option>
                                ))}
                            </select>
                            {isEditing && (
                                <small>Elegí otra categoría para mudar esta subcategoría con todos sus productos.</small>
                            )}
                        </div>
                        <div className={styles['form-group']}>
                            <label>Nombre:</label>
                            <input type="text" name="nombre" value={formData.nombre} onChange={handleChange} required />
                        </div>
                    </div>

                    <div className={styles['form-group']}>
                        <label>URL Imagen (Opcional):</label>
                        <input
                            type="text"
                            name="imagen_url"
                            value={formData.imagen_url}
                            onChange={handleChange}
                            placeholder="https://ejemplo.com/imagen.jpg"
                        />
                        <small style={{ color: '#d6d6d6', fontSize: '0.78rem' }}>{IMAGE_SIZE_HELP}</small>
                    </div>

                    <PrimaryButton type="submit">{isEditing ? 'Actualizar' : 'Crear'}</PrimaryButton>
                    {isEditing && <button type="button" onClick={resetForm} className={styles['btn-secondary']}>Cancelar</button>}
                </form>
            </div>

            <div className={styles['crud-list']}>
                <div className={styles['display-config-header']}>
                    <div>
                        <strong>Visibilidad y orden público</strong>
                        <span>El orden se configura dentro de cada categoría padre.</span>
                    </div>
                    <button type="button" onClick={saveDisplayConfig} className={styles['btn-save-config']} disabled={savingConfig}>
                        <FaSave /> {savingConfig ? 'Guardando...' : 'Guardar orden'}
                    </button>
                </div>
                {configMessage && <div className={styles['success-msg']}>{configMessage}</div>}
                <div className={shared.asyncRegion}>
                {loading ? <p className={shared.loadingState}>Cargando...</p> : (
                    <table className={styles['subcategory-crud-table']}>
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Nombre</th>
                                <th>Categoría Padre</th>
                                <th>Visible</th>
                                <th>Orden</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {subcategories.map(sub => (
                                <tr key={sub.idSubcategoria}>
                                    <td>{sub.idSubcategoria}</td>
                                    <td>{sub.nombre}</td>
                                    <td>{sub.nombre_categoria}</td>
                                    <td>
                                        <button type="button" onClick={() => toggleVisibility(sub.idSubcategoria)} className={styles['btn-visibility']}>
                                            {sub.visible ? <><FaEye /> Visible</> : <><FaEyeSlash /> Oculta</>}
                                        </button>
                                    </td>
                                    <td>
                                        <select
                                            className={styles['order-select']}
                                            value={sub.orden}
                                            onChange={event => changeOrder(sub, Number(event.target.value))}
                                            aria-label={`Orden de ${sub.nombre}`}
                                        >
                                            {subcategories
                                                .filter(item => item.idCategoria === sub.idCategoria)
                                                .map((_, index) => <option key={index} value={index}>{index + 1}</option>)}
                                        </select>
                                    </td>
                                    <td>
                                        <button onClick={() => handleEdit(sub)} className={styles['btn-icon']}><FaEdit /></button>
                                        <button onClick={() => handleDelete(sub.idSubcategoria)} className={`${styles['btn-icon']} ${styles.delete}`}><FaTrash /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
                </div>
            </div>
        </div>
    );
};

export default SubcategoryCrud;
